import Point from "@arcgis/core/geometry/Point";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import PointSymbol3D from "@arcgis/core/symbols/PointSymbol3D";
import { getHighlightMarkerSymbol3D, getHighlightMarkerSymbolProps } from "@components/world-map/world-map-constants";
import { isClusterGraphic } from "@components/world-map/world-map-utils";
export const MARKER_HIGHLIGHT_PROPERTIES = {
  /** Size multiplier applied to the marker while it is highlighted. */
  highlightScale: 1.2
};

/**
 * Returns the highlight overlay symbol. In 3D a `point-3d` symbol is used (recolored
 * and scaled up) so the highlighted pin grows from its bottom anchor while keeping
 * its tip on the coordinate and its grounded or lifted geometry (`isLifted` must
 * match the data markers' current state). In 2D the picture-marker overlay is used.
 * The pulse motion is a separate DOM ring (see `MarkerPulseRing`); this symbol
 * itself is static.
 */
export function getHighlightSymbol(scale = MARKER_HIGHLIGHT_PROPERTIES.highlightScale, is3D = false, isLifted = false) {
  if (is3D) {
    // `PointSymbol3D` fixes its `type` discriminator, so pass only the mutable
    // props to avoid the "cannot assign to read-only property 'type'" warning
    // that `new PointSymbol3D({ type, ... })` triggers.
    const {
      symbolLayers,
      verticalOffset,
      callout
    } = getHighlightMarkerSymbol3D(scale, isLifted);
    return new PointSymbol3D({
      symbolLayers,
      verticalOffset,
      callout
    });
  }
  return new PictureMarkerSymbol(getHighlightMarkerSymbolProps(scale));
}

/**
 * Fully hides (or restores) the underlying data-layer marker for a given entity so
 * it doesn't show behind the animated highlight overlay.
 *
 * Uses the layer view's `filter` to truly remove the feature from rendering
 * (equivalent to `display: none`). `featureEffect` is intentionally not used
 * because it has no effect while clustering (`featureReduction`) is enabled.
 *
 * Pass `null` to clear the filter and show all markers again. Standalone markers
 * are the only ones hidden this way; clustered features aren't rendered
 * individually, so there's nothing to hide for them.
 */
export async function setHiddenHighlightFeature(view, layer, entityId) {
  try {
    const layerView = await view.whenLayerView(layer);
    if (!entityId) {
      layerView.filter = null;
      return;
    }
    const sanitizedId = entityId.replace(/'/g, "''");
    layerView.filter = new FeatureFilter({
      where: `id <> '${sanitizedId}'`
    });
  } catch {
    // The layerview may not exist yet, or the layer/view was removed during
    // navigation (data-layer rebuild, style switch, unmount). There's nothing to
    // hide or restore in that case, so ignore `view:no-layerview-for-layer`.
  }
}

/** Queries the layer for a feature and returns its point geometry, or null. */
export async function getFeaturePoint(layer, entityId) {
  const sanitizedId = entityId.replace(/'/g, "''");
  const results = await layer.queryFeatures({
    where: `id = '${sanitizedId}'`,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by ArcGIS API
    returnGeometry: true
  });
  return results.features[0]?.geometry ?? null;
}

/** Returns the entity IDs of the features aggregated inside the given cluster graphic. */
export async function queryClusterMemberIds(view, layer, clusterGraphic) {
  try {
    const objectId = clusterGraphic.getObjectId();
    if (objectId === null || objectId === undefined) {
      return [];
    }
    const layerView = await view.whenLayerView(layer);
    const result = await layerView.queryFeatures({
      aggregateIds: [objectId]
    });
    return result.features.map(f => String(f.attributes?.id)).filter(Boolean);
  } catch {
    return [];
  }
}

/** Returns the cluster graphic at the given point, or null if the marker is not clustered. */
export async function findClusterGraphic(view, layer, geometry) {
  // The 3D scene renders every marker individually (no cluster feature reduction),
  // so a marker is never clustered. Returning early also avoids `queryAggregates`,
  // which throws on a layer view without clustering enabled.
  if (view.type === "3d") {
    return null;
  }
  const screenPoint = view.toScreen(new Point({
    longitude: geometry.longitude,
    latitude: geometry.latitude
  }));
  if (!screenPoint) {
    return null;
  }
  const hitResult = await view.hitTest(screenPoint, {
    include: [layer]
  });

  // Direct hit on a cluster
  const clusterHit = hitResult.results.find(r => r.type === "graphic" && isClusterGraphic(r.graphic));
  if (clusterHit?.type === "graphic") {
    return clusterHit.graphic;
  }

  // Hit a non-cluster feature → the marker is standalone
  if (hitResult.results.some(r => r.type === "graphic")) {
    return null;
  }

  // Feature not visible at its position → it's aggregated in a cluster elsewhere.
  // Query all visible clusters and return the nearest one using screen distance
  // (aggregates and features may use different spatial references).
  const layerView = await view.whenLayerView(layer);
  const aggregates = await layerView.queryAggregates({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by ArcGIS API
    returnGeometry: true
  });
  let nearest = null;
  let minDistSq = Infinity;
  for (const feature of aggregates.features) {
    const clusterGeom = feature.geometry;
    const clusterScreen = view.toScreen(new Point({
      longitude: clusterGeom.longitude,
      latitude: clusterGeom.latitude
    }));
    if (!clusterScreen) {
      continue;
    }
    const dX = clusterScreen.x - screenPoint.x;
    const dY = clusterScreen.y - screenPoint.y;
    const distSq = dX * dX + dY * dY;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearest = feature;
    }
  }
  return nearest;
}